/**
 * Station radius helper (scale build SC-5a2a, plan §3.3): the gameplay radius R of a station for readers that hold
 * only a layout `StationSite`, a strategic `StationView` or a `NodeRef`, so station ranges are measured from the hull
 * (`surface distance = |p − centre| − R`, the bounding sphere) at any station size.
 *
 * - `siteRadius(site)`: R of the model the runtime shows for a layout site.
 * - `stationRadiusOf(assetId)`: R of a station model by asset id (`StationView.assetId`, or a `NodeRef` reader that
 *   looked its station's asset up).
 * - `siteModelId(site)`: the asset id behind `siteRadius`, for readers that want the entry itself.
 * - `siteBayOffset(site)` (SC-5a2b): the model's `DOCK_BAY_0` socket position (model-local metres), so the hail point
 *   sits on the bay axis even when the bay row is off the centre's height (D-905: the coming Hub's origin is the
 *   envelope centre, 283.6 m above the bay row).
 *
 * R is `stationRadius(entry)` (`space/stations.ts`): |bounds centre| + bounds radius round the model origin, the same
 * number `poseFromEntry` writes to `StationRuntime.radius`, read from the bundled asset manifest (`assets.json`).
 * Career-side readers that hold a `StationRuntime` read `radius` there instead.
 *
 * The home cluster's sites carry the Reach station types' assets (`reach.station_hub` …), but the runtime shows M2's
 * models for them (`station.hub` …; the M2 bridge's `bridgeStations` → `homeStation`). `siteModelId` follows the
 * bridge: a home-cluster site (`anchor.kind` 'cluster', only ever in the home system) whose type is an M2 station id
 * (`HOME_M2_IDS`) takes that M2 station's `assetId` from `stations.json`. A strategic `StationView` of a home station
 * carries the site's asset, so a reader that has the layout site prefers `siteRadius`.
 *
 * An asset missing from the manifest has R 0 (ranges from the centre, as for a `StationRuntime` with no entry),
 * logged once per id. The tables are built once on first use; after that every call is a map lookup and allocates
 * nothing (D-014), so per-tick range checks may call it.
 */
import { HOME_M2_IDS } from '../../bridge/m2/stations'
import { logger } from '../../core/logger'
import { assetsFileSchema } from '../../data/assetSchema'
import assetsJson from '../../data/assets.json'
import { stationsFileSchema } from '../../data/m2Schemas'
import stationsJson from '../../data/stations.json'
import type { StationSite, Vec3T } from '../../galaxy/layout/types'
import { stationRadius } from '../../space/stations'

/** What `siteRadius` and `siteModelId` read of a site. */
export type RadiusSite = Pick<StationSite, 'type' | 'assetId' | 'anchor'>

let radii: ReadonlyMap<string, number> | null = null
let bays: ReadonlyMap<string, Vec3T> | null = null
let homeModels: ReadonlyMap<string, string> | null = null
const warned = new Set<string>()

function radiusTable(): ReadonlyMap<string, number> {
  if (radii === null) {
    const m = new Map<string, number>()
    for (const e of assetsFileSchema.parse(assetsJson).models) m.set(e.id, stationRadius(e))
    radii = m
  }
  return radii
}

/** The bay socket every station model docks at (`StationDef.baySocket`'s default). */
const BAY_SOCKET = 'DOCK_BAY_0'
const NO_OFFSET: Vec3T = Object.freeze([0, 0, 0]) as Vec3T

function bayTable(): ReadonlyMap<string, Vec3T> {
  if (bays === null) {
    const m = new Map<string, Vec3T>()
    for (const e of assetsFileSchema.parse(assetsJson).models) {
      const socket = e.sockets.find((k) => k.name === BAY_SOCKET)
      if (socket !== undefined) m.set(e.id, Object.freeze([socket.position[0], socket.position[1], socket.position[2]]) as Vec3T)
    }
    bays = m
  }
  return bays
}

function homeModelTable(): ReadonlyMap<string, string> {
  if (homeModels === null) {
    const m = new Map<string, string>()
    for (const s of stationsFileSchema.parse(stationsJson).stations) m.set(s.id, s.assetId)
    homeModels = m
  }
  return homeModels
}

/**
 * The gameplay radius R (metres) of station model `assetId`: |bounds centre| + bounds radius round its origin.
 * 0 when the manifest has no such model (logged once).
 */
export function stationRadiusOf(assetId: string): number {
  const r = radiusTable().get(assetId)
  if (r !== undefined) return r
  if (!warned.has(assetId)) {
    warned.add(assetId)
    logger.warn(`siteRadius: no model ${assetId} in the asset manifest; its station ranges count from the centre`)
  }
  return 0
}

/** The asset id of the model the runtime shows for `site`: M2's model for a home-cluster site, else `site.assetId`. */
export function siteModelId(site: RadiusSite): string {
  if (site.anchor.kind === 'cluster' && HOME_M2_IDS.includes(site.type)) {
    const id = homeModelTable().get(site.type)
    if (id !== undefined) return id
  }
  return site.assetId
}

/** The gameplay radius R (metres) of a layout station: `stationRadiusOf(siteModelId(site))`. */
export function siteRadius(site: RadiusSite): number {
  return stationRadiusOf(siteModelId(site))
}

/**
 * The `DOCK_BAY_0` socket position (model-local metres, the model at the site's rotation) of the model the runtime
 * shows for `site`; [0, 0, 0] (the bay axis through the centre) when the model has no bay socket. A map lookup.
 */
export function siteBayOffset(site: RadiusSite): Vec3T {
  return bayTable().get(siteModelId(site)) ?? NO_OFFSET
}
